<template>
  <div class="w-full">
    <div class="relative w-full">
      <!-- Input field -->
      <div v-if="fieldId === 'mobile'" class="flex items-center border-2 rounded-md bg-white" :class="[
        disabled ? 'bg-gray-50 border-gray-200' : 'border-gray-200',
        validationMessage ? 'border-error' : ''
      ]">
        <span class="px-3 select-none text-xs lg:text-base h-full"
          :class="disabled ? 'text-gray-400' : 'text-gray-600'">
          +63
        </span>
        <input maxlength="10" type="tel" v-model="mobileNumber" @input="handleMobileInput" @focus="focused = true"
          @blur="handleBlur" :disabled="disabled"
          class="flex-1 p-3 text-xs lg:text-base bg-transparent outline-none textfield-input" />
      </div>

      <!-- Normal Input -->
      <input v-else :maxlength="maxLength" :id="fieldId"
        :type="isVisible ? 'text' : (is_password || is_con_password || is_pin) ? 'password' : 'text'"
        v-model="inputValue" @input="handleInput" @focus="focused = true" @blur="handleBlur"
        @keydown.enter.prevent="focusNextOrSubmit" :disabled="disabled" autocomplete="off"
        class="textfield-input w-full p-3 lg:p-3 text-xs lg:text-base border-2 rounded-md" :class="[
          validationMessage ? 'border-error' : 'border-gray-200',
          disabled ? 'bg-gray-50' : 'bg-white'
        ]" :placeholder="focused || inputValue ? placeholder : ''" />

      <!-- Floating label -->
      <label :for="fieldId" :class="[
        'textfield-label absolute px-1 text-xs lg:text-base',
        (focused || inputValue || (fieldId === 'mobile' && mobileNumber)) ? 'label-floating text-primary' : '',
        disabled ? 'label-disabled text-grey border-gray-300' : '',
        fieldId === 'mobile' ? 'bg-white' : ''
      ]">
        {{ label }} <span v-if="required" class="text-error">*</span>
      </label>
    </div>

    <!-- Validation Message -->
    <p v-if="validationMessage" class="text-xs text-error mt-1">{{ validationMessage }}</p>
  </div>
</template>

<script>
export default {
  name: "v-text",
  props: {
    label: String,
    placeholder: String,
    is_pin: Boolean,
    is_password: Boolean,
    is_con_password: Boolean,
    is_account: Boolean,
    is_username: Boolean,
    is_creation: Boolean,
    modelValue: String,
    disabled: Boolean,
    enableValidation: { type: Boolean, default: false },
    maxLength: { type: Number, default: 50 },
    fieldId: { type: String, required: true },
    required: { type: Boolean, default: false },
    passwordValue: { type: String, default: "" },
  },
  data() {
    return {
      inputValue: this.modelValue || "",
      mobileNumber: "",
      isVisible: false,
      validationMessage: "",
      focused: false,
    };
  },
  watch: {
    modelValue(newValue) {
    const value = newValue ?? "";  // ensure string
    if (this.fieldId === "mobile" && value.startsWith("+63")) {
      this.mobileNumber = value.replace("+63", "");
    } else {
      this.inputValue = value;
    }
  },

    inputValue() {
      if (this.enableValidation) this.validateField();
    },

    mobileNumber() {
      if (this.enableValidation) this.validateField();
    },
  },
  mounted() {
    const value = this.modelValue ?? ""; // ensure string
  if (this.fieldId === "mobile" && value.startsWith("+63")) {
    this.mobileNumber = value.replace("+63", "");
  }
  },
  methods: {
    handleMobileInput(event) {
      let value = event.target.value.replace(/\D/g, "");
      if (value.length > 10) value = value.slice(0, 10);
      this.mobileNumber = value;
      this.$emit("update:modelValue", `+63${value}`);
    },

    handleInput(event) {
      let value = event.target.value;

      if (this.fieldId === "account_no" || this.fieldId === "serial_no") {
        value = value
          .toUpperCase()
          .replace(/\s+/g, "")
          .replace(/[^A-Z0-9-]/g, "");
      }
      if (this.fieldId === "email") value = value.replace(/\s+/g, "");
      if (this.fieldId === "username") value = value.toUpperCase().replace(/\s+/g, "");
      if (this.fieldId === "age") value = value.replace(/\D+/g, "");
      if (["fname", "mname", "lname", "name"].includes(this.fieldId)) value = value.toUpperCase();

      this.inputValue = value;
      this.$emit("update:modelValue", value);
    },

    handleBlur() {
      this.focused = false;
      if (this.enableValidation) this.validateField();
    },

    validateField() {
      if (this.required && !this.inputValue && this.fieldId !== "mobile" && !this.mobileNumber) {
        this.validationMessage = "This field is required.";
        return;
      }

      switch (this.fieldId) {
        case "email":
          if (this.inputValue && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(this.inputValue))
            this.validationMessage = "Please enter a valid email address.";
          else
            this.validationMessage = "";
          break;

        case "mobile":
          if (this.mobileNumber) {
            if (!/^9\d{9}$/.test(this.mobileNumber)) {
              this.validationMessage = "Mobile number must start with 9 and be 10 digits.";
            } else {
              this.validationMessage = "";
            }
          } else if (this.required) {
            this.validationMessage = "This field is required.";
          } else {
            this.validationMessage = "";
          }
          break;

        case "age": {
          const ageValue = Number(this.inputValue); // ✅ now legal
          if (this.inputValue && isNaN(ageValue)) {
            this.validationMessage = "Please enter a valid number for age.";
          } else if (this.inputValue && ageValue < 12) {
            this.validationMessage = "Age must be 12 or above.";
          } else if (!this.inputValue && this.required) {
            this.validationMessage = "This field is required.";
          } else {
            this.validationMessage = "";
          }
          break;
        }


        default:
          this.validationMessage = "";
      }
    },


    focusNextOrSubmit(event) {
      const form = event.target.form || document;
      const elements = Array.from(form.querySelectorAll("input, select, textarea, button"))
        .filter(el => !el.disabled && el.type !== "hidden");

      const index = elements.indexOf(event.target);

      if (index > -1 && index + 1 < elements.length) {
        const nextEl = elements[index + 1];
        nextEl.type === "submit" ? nextEl.click() : nextEl.focus();
      } else {
        const submitBtn = form.querySelector("button[type='submit'], input[type='submit']");
        if (submitBtn) submitBtn.click();
      }
    },
  },
};
</script>

<style scoped>
.textfield-input {
  transition: border-color 0.3s;
}

.textfield-input:focus {
  border-color: #016B00;
  outline: none;
}

.textfield-input:disabled {
  background-color: #f8f8f8;
  color: #999;
  cursor: not-allowed;
}

.textfield-label {
  left: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  transition: all 0.2s ease-in-out;
  pointer-events: none;
}

.label-floating {
  top: 0;
  transform: translateY(-50%) scale(1);
  color: #016B00;
  background-color: white;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
select:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: #333333 !important;
  caret-color: #333333 !important;
  transition: background-color 9999s ease-in-out 0s !important;
}

input:disabled:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px #f8f8f8 inset !important;
}
</style>
